import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Loader, Stack, Text } from "@mantine/core";
import styles from "./NavigationLoadingOverlay.module.css";

interface NavigationLoadingState {
  active: boolean;
  title: string | null;
  detail: string | null;
}

interface NavigationLoadingContextValue extends NavigationLoadingState {
  showNavigationLoading: (title: string, detail?: string) => void;
  hideNavigationLoading: () => void;
}

/**
 * Safety auto-hide window: the overlay self-clears after this long, even if
 * nobody calls hideNavigationLoading(). Covers route-guard redirects, errors
 * before the destination's try/catch, or pages that never mount. Tuned long
 * enough to comfortably cover a slow appointment fetch (which is the
 * canonical use today) but short enough that a stuck overlay never traps
 * the user.
 */
const SAFETY_AUTO_HIDE_MS = 10_000;

const NavigationLoadingContext =
  createContext<NavigationLoadingContextValue | null>(null);

export const NavigationLoadingProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, setState] = useState<NavigationLoadingState>({
    active: false,
    title: null,
    detail: null,
  });
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSafetyTimer = useCallback(() => {
    if (safetyTimerRef.current !== null) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  }, []);

  const hideNavigationLoading = useCallback(() => {
    clearSafetyTimer();
    setState({ active: false, title: null, detail: null });
  }, [clearSafetyTimer]);

  const showNavigationLoading = useCallback(
    (title: string, detail?: string) => {
      clearSafetyTimer();
      setState({ active: true, title, detail: detail ?? null });
      safetyTimerRef.current = setTimeout(() => {
        safetyTimerRef.current = null;
        setState({ active: false, title: null, detail: null });
      }, SAFETY_AUTO_HIDE_MS);
    },
    [clearSafetyTimer],
  );

  useEffect(() => {
    return () => {
      clearSafetyTimer();
    };
  }, [clearSafetyTimer]);

  const value = useMemo(
    () => ({ ...state, showNavigationLoading, hideNavigationLoading }),
    [state, showNavigationLoading, hideNavigationLoading],
  );

  return (
    <NavigationLoadingContext.Provider value={value}>
      {children}
    </NavigationLoadingContext.Provider>
  );
};

export const useNavigationLoading = (): NavigationLoadingContextValue => {
  const ctx = useContext(NavigationLoadingContext);
  if (!ctx) {
    throw new Error(
      "useNavigationLoading must be used inside NavigationLoadingProvider",
    );
  }
  return ctx;
};

export const NavigationLoadingOverlay: React.FC = () => {
  const { active, title, detail } = useNavigationLoading();
  if (!active) return null;
  return (
    <Box
      className={styles.overlay}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Stack gap="md" align="center" maw={480} px="md">
        <Loader size="lg" color="var(--brand-teal)" />
        {title && (
          <Text fw={600} size="lg" ta="center">
            {title}
          </Text>
        )}
        {detail && (
          <Text size="sm" c="dimmed" ta="center">
            {detail}
          </Text>
        )}
      </Stack>
    </Box>
  );
};
